import * as Res from ".";
import * as E from "fp-ts/Either";
import { Fold, Getter, Setter, Traversal, } from "@discovery/prelude/lib/control/lens";
import * as RA from "fp-ts/ReadonlyArray";
import * as RNEA from "fp-ts/ReadonlyNonEmptyArray";
import { pipe } from "fp-ts/function";
const unsafeReportErrors = (r) => {
    Res.foldExceptionError((e) => {
        if (e.kind == "ValidationException") {
            console.warn(e);
        }
    }, console.warn, () => { }, r);
    return r;
};
export const _Response = () => Traversal.traversal(Fold.fold((fold, empty, r) => Res.reduce((a, b) => fold(b, a), empty, process.env.NODE_ENV === "development" ? unsafeReportErrors(r) : r)), Setter.setter((g) => (r) => Res.map(g, r), (x) => (r) => Res.map(() => x, r)));
export const _Exception = () => Fold.fold((fold, empty, r) => Res.foldResponseException((b, a) => fold(a, b), empty, r));
export const _Error = () => Fold.fold((fold, empty, r) => Res.foldError((b, a) => fold(a, b), empty, r));
export const _Errors = () => Fold.concat(Fold.map(RNEA.of, _Exception()), _Error());
/**
 * Optics for relationships and is used whenever the relationship is mapped to a single type
 *
 * Example: Relationship `routes` targets `routes`
 *
 * ```typescript
 * Fold.compose(
 *   _PageGetter,
 *   _Rel("routes")
 * )
 * ```
 */
export const _Rel = (rel) => Getter.getter((r) => Res.lookupRel(rel, r));
/**
 * Optics for relationships and is used whenever the relationship is a sumtype and not a single type
 *
 * Example: Relationship `primaryContent` targets `taxonomyNode | video | article ...` but we want `taxonomyNode`
 *
 * ```typescript
 * Fold.compose(
 *   _PageGetter,
 *   _RelAssoc("primaryContent", "taxonomyNode")
 * )
 * ```
 */
export const _RelAssoc = (rel, assoc) => Getter.getter((r) => Res.lookupRelAssoc(rel, r)(assoc));
/**
 * Optic for alternating optics that result in responses. Will take the first
 * matching optic and skip the rest. Similar to `Fold.alt` but is specifically
 * for `Response`
 *
 * Example: Grab the image of if it is a Collection -> Video | Channel | Show | Article -> Image
 * Priority is `Video - Channel - Show - Article` in this example
 *
 * ```typescript
 * Fold.compose(
 *   _PageGetter,
 *   _Alt(
 *     _Col2VideoImage,
 *     _Col2ChannelImage,
 *     _Col2ShowImage,
 *     _Col2ArticleImage,
 *   )
 * )
 * ```
 *
 * @param fs Lenses to apply to `S`
 * @return A Fold<S, A>
 */
export const _Alt = (...fs) => Fold.fold((fold, empty, s) => {
    for (const f of fs) {
        // apply the lens fs[i]
        const vals = Fold.toArray(f, s);
        // if the vals is empty, nothing was found so continue to lens f[i+1]
        if (RA.isEmpty(vals))
            continue;
        // if every value in vals is `Right` in its Response, this is all OK so return here
        if (pipe(vals, RA.every(({ response: [_, __, v] }) => E.isRight(v) && E.isRight(v.right)))) {
            return RA.reduceRight(empty, fold)(vals);
        }
    }
    return empty;
});
/** Used for transforming a ReadonlyArray<Response<List<A>> -> Lens<ReadonlyArray<Response<A>>>
 * This is commonly used when we need to preserve the included array in Response
 * for further relationship lookup
 *
 * Example:
 *
 * ```typescript
 * // Lens<Response<ReadonlyArray<Item>>> -> Lens<ReadonlyArray<Response<A>>>
 * Fold.compose(_ItemList, _SequenceArray)
 * ```
 */
export const _SequenceArray = () => Getter.getter(Res.sequenceArray);
/** Used for transforming a Lens<Response<List<A>> -> Lens<Response<A>>
 * Compared to _SequenceList, this also iterates over each item thus
 * allowing to work directly with the Response<A>
 *
 * Example:
 *
 * ```typescript
 * // Lens<Response<List<Item>>> -> Lens<Response<A>>
 * Fold.compose(_ItemList, _ForEach)
 * ```
 */
export const _ForEach = () => Fold.compose(_SequenceArray(), Fold._each());

